/**
 * @author: Claude
 * @date: 2026/03/13
 * @desc: 组件文档页面主入口
 */
import React, { useMemo, useState, useEffect } from 'react';
import { Layout } from 'tea-component';
import { useHistory, useLocation } from 'umi';
import ComponentTree from './components/ComponentTree';
import ComponentPreview from './components/ComponentPreview';
import { COMPONENT_TREE, type IComponentInfo } from './config/demos';

import type { ITreeNode } from './components/ComponentTree/types';

import './index.scss';

const { Sider, Content } = Layout;

function filterDocumentedComponents(components: IComponentInfo[]): IComponentInfo[] {
  return components.reduce<IComponentInfo[]>((result, component) => {
    if (component.type === 'single') {
      if (component.hasDemo || component.hasReadme) {
        result.push(component);
      }

      return result;
    }

    const children = component.children ? filterDocumentedComponents(component.children) : [];

    if (children.length > 0) {
      result.push({
        ...component,
        children,
      });
    }

    return result;
  }, []);
}

function sortComponents(components: IComponentInfo[]): IComponentInfo[] {
  return [...components]
    .map((component) => {
      if (!component.children || component.children.length === 0) {
        return component;
      }

      return {
        ...component,
        children: sortComponents(component.children),
      };
    })
    .sort((prevComponent, nextComponent) => {
      // if (prevComponent.type !== nextComponent.type) {
      //   return prevComponent.type === 'composite' ? -1 : 1;
      // }

      return prevComponent.name.localeCompare(nextComponent.name);
    });
}

/**
 * 将组件配置转换为树节点数据
 */
function convertToTreeNodes(components: IComponentInfo[]): ITreeNode[] {
  return components.map((component) => ({
    key: component.path,
    title: component.name,
    isLeaf: component.type === 'single',
    componentInfo: component,
    children: component.children ? convertToTreeNodes(component.children) : undefined,
  }));
}

/**
 * 查找组件信息
 */
function findComponentInfo(nodes: ITreeNode[], key: string): IComponentInfo | undefined {
  for (const node of nodes) {
    if (node.key === key) {
      return node.componentInfo;
    }
    if (node.children) {
      const found = findComponentInfo(node.children, key);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
}

/**
 * 查找树节点
 */
function findTreeNode(nodes: ITreeNode[], key: string): ITreeNode | null {
  for (const node of nodes) {
    if (node.key === key) {
      return node;
    }
    if (node.children) {
      const found = findTreeNode(node.children, key);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

/**
 * 查找第一个叶子节点
 */
function findFirstLeafNode(nodes: ITreeNode[]): ITreeNode | null {
  for (const node of nodes) {
    if (node.isLeaf) {
      return node;
    }
    if (node.children && node.children.length > 0) {
      const found = findFirstLeafNode(node.children);
      if (found) return found;
    }
  }
  return null;
}

/**
 * 根据 menu 参数查找最终应该展示的叶子节点
 */
function findTargetLeafNode(nodes: ITreeNode[], menuKey: string | null): ITreeNode | null {
  if (!menuKey) {
    return findFirstLeafNode(nodes);
  }

  const matchedNode = findTreeNode(nodes, menuKey);
  if (!matchedNode) {
    return findFirstLeafNode(nodes);
  }

  if (matchedNode.isLeaf) {
    return matchedNode;
  }

  if (matchedNode.children && matchedNode.children.length > 0) {
    return findFirstLeafNode(matchedNode.children);
  }

  return findFirstLeafNode(nodes);
}

/**
 * 组件文档页面
 */
const ComponentDocs: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // 树节点数据
  const treeData = useMemo(() => {
    return convertToTreeNodes(sortComponents(filterDocumentedComponents(COMPONENT_TREE)));
  }, []);

  const menuKey = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('menu');
  }, [location.search]);

  // 根据 url 同步选中项，并在缺省或无效时纠正 menu 参数
  useEffect(() => {
    if (treeData.length === 0) {
      return;
    }

    const targetLeafNode = findTargetLeafNode(treeData, menuKey);
    if (!targetLeafNode) {
      return;
    }

    if (selectedKey !== targetLeafNode.key) {
      setSelectedKey(targetLeafNode.key);
    }

    if (menuKey !== targetLeafNode.key) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('menu', targetLeafNode.key);

      history.replace({
        pathname: location.pathname,
        search: `?${searchParams.toString()}`,
      });
    }
  }, [history, location.pathname, location.search, menuKey, selectedKey, treeData]);

  // 当前选中的组件信息
  const currentComponent = useMemo(() => {
    if (!selectedKey) {
      return null;
    }
    return findComponentInfo(treeData, selectedKey) || null;
  }, [selectedKey, treeData]);

  // 处理选中
  const handleSelect = (key: string) => {
    const searchParams = new URLSearchParams(location.search);

    setSelectedKey(key);

    if (searchParams.get('menu') === key) {
      return;
    }

    searchParams.set('menu', key);

    history.push({
      pathname: location.pathname,
      search: `?${searchParams.toString()}`,
    });
  };

  return (
    <Layout className="dbi-component-docs">
      {/* 左侧目录树 */}
      <Sider className="dbi-component-docs__sider">
        <div className="dbi-component-docs__sider-header">
          <h2>组件目录</h2>
        </div>
        <ComponentTree treeData={treeData} selectedKey={selectedKey} onSelect={handleSelect} />
      </Sider>

      {/* 右侧文档展示区 */}
      <Content className="dbi-component-docs__content">
        {currentComponent ? (
          <ComponentPreview componentInfo={currentComponent} />
        ) : (
          <div className="dbi-component-docs__placeholder">
            <div className="dbi-component-docs__placeholder-icon">📦</div>
            <div className="dbi-component-docs__placeholder-text">请从左侧选择一个组件查看文档</div>
          </div>
        )}
      </Content>
    </Layout>
  );
};

export default ComponentDocs;
